/**
 * FieldSitesList (Inventory Screens Step 5.1) — tappable site cards for the
 * field zone's 'sites' view. Reads the get_field_sites card shape persisted
 * in tmg-field ({site_id, site_name, site_city, site_state_province,
 * site_modules, tool_count, found_count}) — SEVEN keys since the backend
 * added found_count. Tap → parent runs the records fan-out and flips to
 * the 'records' view.
 *
 * found_count arrived AFTER tool_count, so it must be treated as possibly
 * absent. persistLoadResponse stores the response wholesale (fieldStore.js
 * :191, a bulkPut with no key whitelist), which is why no persistence change
 * was needed — but it also means a device still carrying a payload cached
 * before the backend change has sites with no found_count at all. Absent
 * falls back to the plain record count, never "0 of 86 · 0%".
 */

import React, { useMemo, useState } from 'react';
import './FieldSitesList.css';

// Sort key mirrors the server's `(s.get("site_name") or "").lower()`
// (function_app.py:10087) so the client re-establishes the wire order rather
// than inventing a different one — Dexie's toArray() returns primary-key
// order and discards the server's sort entirely.
//
// Plain codepoint comparison on the lowercased string, deliberately NOT
// localeCompare: locale collation orders case and punctuation differently from
// Python's compare, so it would drift from the server on exactly the inputs
// that look already-sorted (observed server order: TENMA, Test Site A,
// TMG Dearborn).
//
// ONE deliberate divergence: an EMPTY site_name falls back to site_id here,
// where the server sorts it under ''. A nameless card displays its id, so
// ordering by what the rep can actually read beats ordering by a blank.
const sortKey = (s) => String(s.site_name || s.site_id || '').toLowerCase();

function bySiteName(a, b) {
  const ka = sortKey(a);
  const kb = sortKey(b);
  if (ka < kb) return -1;
  if (ka > kb) return 1;
  return 0;
}

/**
 * The count line.
 *
 * PERCENTAGE CLAMP — load-bearing, do not simplify. 100% must mean complete.
 * Bardstown is 839 of 841; Math.round alone renders that as 100%, a card
 * claiming a finished site while two tools are still unfound. Only an exact
 * found === total earns 100.
 *
 * found_count absent / null / 0 falls through to the record count. Absent is
 * the cached-payload case above; 0 is a real site nobody has started, and
 * '0 of 86 · 0%' says less than '86 records' while looking like progress.
 */
function countLabel(site) {
  const total = site.tool_count ?? 0;
  const found = site.found_count;
  if (total === 0) return '0 records';                       // no division
  if (found === null || found === undefined || found === 0) {
    return `${total} records`;
  }
  const pct = (found === total)
    ? 100
    : Math.min(99, Math.round((found / total) * 100));
  return `${found} of ${total} · ${pct}%`;
}

// Case-insensitive substring across the three fields the card displays.
// An empty or whitespace-only query matches everything.
function matchesQuery(site, q) {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return [site.site_name, site.site_city, site.site_state_province]
    .some((v) => v && String(v).toLowerCase().includes(needle));
}

export default function FieldSitesList({ sites, loadingSiteId, onSelect, onDetail }) {
  const [query, setQuery] = useState('');

  // Sorts a COPY. filter() already returns a new array, so the props array is
  // never mutated — it belongs to the host's state.
  const visible = useMemo(() => {
    const rows = (sites || []).filter((s) => matchesQuery(s, query));
    rows.sort(bySiteName);
    return rows;
  }, [sites, query]);

  // "Nothing on this device" is a DIFFERENT fact from "nothing matched your
  // search", so it keeps its own copy and renders with no search box — there
  // is nothing to search. Reusing this line for a no-match would tell a rep
  // their data was gone.
  if (!sites || sites.length === 0) {
    return (
      <p className="fsl-empty" data-testid="field-sites-empty">
        No sites on device yet{'—'}connect and reload to pull your site list.
      </p>
    );
  }

  return (
    <div className="fsl-wrap" data-testid="field-sites-wrap">
      <input
        type="search"
        className="fsl-search"
        data-testid="field-sites-search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search sites"
        aria-label="Search sites"
      />

      {visible.length === 0 ? (
        <p className="fsl-empty" data-testid="field-sites-no-match">
          No sites match “{query.trim()}”
        </p>
      ) : (
        <div className="fsl" data-testid="field-sites-list">
          {visible.map((site) => {
            const loading = loadingSiteId === site.site_id;
            const busy = loadingSiteId != null;
            return (
              /* STRUCTURE: a div wrapping TWO SIBLING buttons, not one button
                 with a nested button (invalid HTML). Chosen over a delegated
                 click + stopPropagation because that route needs the DETAIL
                 control to be a non-button — a span with role="button" —
                 which gives up native Enter/Space activation and focus
                 handling. Two real buttons keep both controls keyboard-
                 operable. `disabled` does not apply to a div, so the dimmed
                 busy state moves from :disabled to a modifier class. */
              <div
                key={site.site_id}
                className={`fsl-card${busy ? ' fsl-card-busy' : ''}`}
                data-testid="field-site-card"
              >
                <button
                  type="button"
                  className="fsl-card-tap"
                  disabled={busy}
                  onClick={() => onSelect(site)}
                  data-testid="field-site-tap"
                >
                  <span className="fsl-card-main">
                    <span className="fsl-card-name">{site.site_name || site.site_id}</span>
                    <span className="fsl-card-sub">
                      {[site.site_city, site.site_state_province].filter(Boolean).join(', ')}
                    </span>
                  </span>
                  <span className="fsl-card-count">
                    {loading ? 'Loading…' : countLabel(site)}
                  </span>
                </button>

                {/* WIRING ONLY — no detail view exists in this unit. With no
                    onDetail prop the control is not rendered at all, which is
                    the current state: FieldZonePlaceholder passes onSelect
                    only. */}
                {onDetail && (
                  <button
                    type="button"
                    className="fsl-card-detail"
                    disabled={busy}
                    onClick={() => onDetail(site)}
                    data-testid="field-site-detail"
                  >
                    DETAIL
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
